import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Loader2, Images } from "lucide-react";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";
import { lockBodyScroll, unlockBodyScroll } from "../lib/scrollLock";

/**
 * MediaPicker — modal grid that lets the admin pick an existing
 * uploaded image instead of re-uploading the same file. Defaults to
 * the "used_first" sort so the assets you actually reach for surface
 * to the top before unused orphans.
 *
 * Usage:
 *   <MediaPicker
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onSelect={(url) => { setSomething(url); setOpen(false); }}
 *     accept="image"  // or "video" or "all"
 *   />
 */
export default function MediaPicker({ open, onClose, onSelect, accept = "image" }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchMedia = async (newOffset = 0, append = false) => {
    if (!open) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setItems(null);
    }
    setError("");
    try {
      const r = await api.get(
        `/admin/media?limit=60&offset=${newOffset}&q=${encodeURIComponent(q)}&sort=newest&include_usage=false`
      );
      if (append) {
        setItems((prev) => [...(prev || []), ...(r.data?.items || [])]);
      } else {
        setItems(r.data?.items || []);
      }
      setTotal(r.data?.total || 0);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMedia(0, false);
    }
  }, [open, q]);

  // Lock body scroll while the modal is open so the page underneath
  // doesn't dance about when the visitor scrolls inside the picker.
  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((m) => {
      if (accept === "image" && !(m.content_type || "").startsWith("image/")) return false;
      if (accept === "video" && !(m.content_type || "").startsWith("video/")) return false;
      return true;
    });
  }, [items, accept]);

  const hasNext = items && items.length < total;

  const loadMore = () => {
    if (loadingMore || !items) return;
    fetchMedia(items.length, true);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0A0F17]/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Pick an image from the media library"
      data-testid="media-picker"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-md border border-black/10 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-black/10 px-5 py-3">
          <div className="flex items-center gap-2">
            <Images size={16} strokeWidth={2.5} className="text-brand-accent" />
            <span className="font-display text-sm font-bold">Pick from media library</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="media-picker-close"
            aria-label="Close picker"
            className="rounded-full p-1 text-[var(--brand-body)] hover:bg-black/5"
          >
            <X size={16} />
          </button>
        </header>

        <div className="border-b border-black/10 bg-[#F8FAFC] px-5 py-3">
          <label className="flex items-center gap-2">
            <Search size={14} className="text-[var(--brand-body)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search filenames…"
              data-testid="media-picker-search"
              className="flex-1 rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
            />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error ? (
            <p className="mb-3 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm">
              {error}
            </p>
          ) : null}
          {loading ? (
            <div className="flex h-32 items-center justify-center gap-2 text-sm text-[var(--brand-body)]">
              <Loader2 size={14} className="animate-spin" /> Loading library…
            </div>
          ) : filtered.length === 0 ? (
            <p
              className="py-10 text-center text-sm text-[var(--brand-body)]"
              data-testid="media-picker-empty"
            >
              {q ? `No matches for "${q}".` : "No images in the library yet — upload one below the picker."}
            </p>
          ) : (
            <>
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {filtered.map((m) => {
                  const url = resolveAsset(m.url);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(m.url)}
                        data-testid={`media-picker-item-${m.id}`}
                        className="group flex w-full flex-col overflow-hidden rounded-md border border-black/10 text-left hover:border-[#0A0F17]"
                      >
                        <span className="relative block aspect-square w-full bg-[#F8FAFC]">
                          <img
                            src={url}
                            alt={m.original_filename || ""}
                            loading="lazy"
                            className="absolute inset-0 m-auto h-full w-full object-cover"
                          />
                        </span>
                        <span className="flex flex-col gap-0.5 px-2 py-1.5">
                          <span
                            className="truncate font-display text-xs font-bold text-[var(--brand-ink)]"
                            title={m.original_filename || ""}
                          >
                            {m.original_filename || "(no name)"}
                          </span>
                          <span className="text-[10px] text-[var(--brand-body)]">
                            {formatBytes(m.size)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {hasNext ? (
                <div className="mt-6 flex justify-center pb-4">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="media-picker-load-more"
                    className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] hover:bg-brand-accent px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white transition disabled:opacity-40"
                  >
                    {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
                    Load more
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatBytes(b) {
  if (!b) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}
